/**
 * Frame presets for per-component outer frame/background
 * Maps preset IDs to CSS variable values for overriding trait/state/chart frame styles
 */
import type { CSSProperties } from 'react'
import type { FramePreset } from '../../services/appfactoryFirestore'

export const FRAME_PRESET_OPTIONS: { value: FramePreset; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'Glass', label: 'Glass' },
  { value: 'GlassV2', label: 'GlassV2' },
  { value: 'LiquidGlass', label: 'LiquidGlass' },
  { value: 'Solid', label: 'Solid' },
  { value: 'Minimal', label: 'Minimal' },
  { value: 'FrostedLight', label: 'FrostedLight' },
]

export type FrameStyleVars = {
  '--frame-bg': string
  '--frame-border': string
  '--frame-shadow': string
  '--frame-radius': string
  '--frame-blur': string
  '--frame-padding': string
}

/** Default 的初始 frame 變數，可透過視覺編輯器自訂 */
export const DEFAULT_FRAME_VARS: FrameStyleVars = {
  '--frame-bg': 'transparent',
  '--frame-border': '1px solid rgba(255, 255, 255, 0.12)',
  '--frame-shadow': 'none',
  '--frame-radius': '2rem',
  '--frame-blur': 'none',
  '--frame-padding': '24px',
}

export const PRESET_STYLES: Record<Exclude<FramePreset, 'default'>, FrameStyleVars> = {
  Glass: {
    '--frame-bg': 'rgba(255, 255, 255, 0.06)',
    '--frame-border': '1px solid rgba(255, 255, 255, 0.12)',
    '--frame-shadow': '0 8px 32px rgba(0, 0, 0, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.08)',
    '--frame-radius': '2rem',
    '--frame-blur': 'blur(24px)',
    '--frame-padding': '24px',
  },
  GlassV2: {
    '--frame-bg': 'rgba(255, 255, 255, 0.12)',
    '--frame-border': '1px solid rgba(255, 255, 255, 0.2)',
    '--frame-shadow': '0 8px 32px rgba(0, 0, 0, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.15)',
    '--frame-radius': '2rem',
    '--frame-blur': 'blur(24px)',
    '--frame-padding': '24px',
  },
  LiquidGlass: {
    '--frame-bg':
      'linear-gradient(135deg, rgba(255, 255, 255, 0.18) 0%, rgba(255, 255, 255, 0.08) 44%, rgba(255, 255, 255, 0.04) 100%)',
    '--frame-border': '1px solid rgba(255, 255, 255, 0.3)',
    '--frame-shadow':
      '0 18px 40px rgba(8, 15, 40, 0.42), inset 0 1px 0 rgba(255, 255, 255, 0.42), inset 0 -10px 18px rgba(255, 255, 255, 0.08)',
    '--frame-radius': '2rem',
    '--frame-blur': 'blur(18px) saturate(175%)',
    '--frame-padding': '24px',
  },
  Solid: {
    '--frame-bg': 'rgba(30, 41, 59, 0.95)',
    '--frame-border': '1px solid rgba(148, 163, 184, 0.2)',
    '--frame-shadow': '0 2px 8px rgba(0, 0, 0, 0.4)',
    '--frame-radius': '2rem',
    '--frame-blur': 'none',
    '--frame-padding': '24px',
  },
  Minimal: {
    '--frame-bg': 'transparent',
    '--frame-border': '1px solid rgba(255, 255, 255, 0.08)',
    '--frame-shadow': 'none',
    '--frame-radius': '2rem',
    '--frame-blur': 'none',
    '--frame-padding': '24px',
  },
  FrostedLight: {
    '--frame-bg': 'rgba(255, 255, 255, 0.6)',
    '--frame-border': '1px solid rgba(148, 163, 184, 0.15)',
    '--frame-shadow': '0 4px 24px rgba(0, 0, 0, 0.04)',
    '--frame-radius': '20px',
    '--frame-blur': 'blur(20px) saturate(150%)',
    '--frame-padding': '20px',
  },
}

export function getFrameStyle(preset: FramePreset): CSSProperties | undefined {
  const vars = preset === 'default' ? DEFAULT_FRAME_VARS : PRESET_STYLES[preset]
  return vars as unknown as CSSProperties
}

/** 取得指定 preset 的初始 vars（含 default）。自訂 preset 請傳入 overrides 取得。 */
export function getPresetVars(preset: FramePreset): FrameStyleVars {
  return (preset === 'default' ? DEFAULT_FRAME_VARS : PRESET_STYLES[preset]) as FrameStyleVars
}

/** 取得 framer 編輯用初始 vars（含 built-in 與自訂 preset） */
export function getFramerInitialVars(
  presetId: string,
  overrides: Record<string, FrameStyleVars>
): FrameStyleVars {
  const base = presetId === 'default' ? DEFAULT_FRAME_VARS : PRESET_STYLES[presetId as FramePreset]
  return { ...(base ?? DEFAULT_FRAME_VARS), ...(overrides[presetId] ?? {}) } as FrameStyleVars
}

// ---------------------------------------------------------------------------
// Framer Preset Variable Tuner helpers
// ---------------------------------------------------------------------------

/** Parse blur pixel value from a backdrop-filter string like "blur(18px) saturate(175%)" */
export function parseBlurValue(val: string | undefined): number {
  const match = String(val || '').match(/blur\((\d+)px\)/)
  return match ? parseInt(match[1], 10) : 0
}

/** Parse saturate percentage from a backdrop-filter string like "blur(18px) saturate(175%)" */
export function parseSaturateValue(val: string | undefined): number {
  const match = String(val || '').match(/saturate\((\d+)%?\)/)
  return match ? parseInt(match[1], 10) : 100
}

/** Build a backdrop-filter value from blur px and saturate pct */
export function buildBlurValue(blurPx: number, saturatePct: number): string {
  if (blurPx <= 0 && saturatePct === 100) return 'none'
  const parts: string[] = []
  if (blurPx > 0) parts.push(`blur(${blurPx}px)`)
  if (saturatePct !== 100) parts.push(`saturate(${saturatePct}%)`)
  return parts.length > 0 ? parts.join(' ') : 'none'
}

/** Parse the first rgba opacity from a background value (may contain linear-gradient) */
export function parseBgOpacity(val: string | undefined): number {
  const s = String(val || '')
  if (s === 'transparent' || !s) return 0
  const match = s.match(/rgba\(\s*\d+\s*,\s*\d+\s*,\s*\d+\s*,\s*([\d.]+)\s*\)/)
  return match ? Math.round(parseFloat(match[1]) * 100) : 100
}

/** Replace all rgba opacity values in a background string */
export function adjustBgOpacity(val: string | undefined, opacityPct: number): string {
  const opacity = Math.max(0, Math.min(100, opacityPct)) / 100
  const s = String(val || 'transparent')
  if (s === 'transparent' && opacity <= 0) return 'transparent'
  return s.replace(
    /rgba\((\s*\d+\s*,\s*\d+\s*,\s*\d+\s*),\s*[\d.]+\s*\)/g,
    `rgba($1, ${opacity})`
  )
}

/** Parse border width from a border shorthand like "1px solid rgba(…)" */
export function parseBorderWidth(val: string | undefined): number {
  const match = String(val || '').match(/^([\d.]+)px/)
  return match ? parseFloat(match[1]) : 0
}

/** Replace the leading pixel width in a border shorthand */
export function adjustBorderWidth(val: string | undefined, widthPx: number): string {
  return String(val || '0px solid transparent').replace(/^[\d.]+px/, `${widthPx}px`)
}

/** Parse radius as a plain px number (handles "2rem" by converting to 32px) */
export function parseRadiusPx(val: string | undefined): number {
  const s = String(val || '').trim()
  const remMatch = s.match(/^([\d.]+)rem$/)
  if (remMatch) return Math.round(parseFloat(remMatch[1]) * 16)
  const pxMatch = s.match(/^([\d.]+)(px)?$/)
  if (pxMatch) return Math.round(parseFloat(pxMatch[1]))
  return 0
}

/** 將 frame preset 轉成可編輯的 CSS 字串，供 CSS Editor 顯示 */
export function getFramePresetAsCss(preset: FramePreset): string {
  if (preset === 'default') {
    return `/* Framer - Default：使用元件內建樣式，無額外覆蓋 */`
  }
  const vars = PRESET_STYLES[preset]
  const lines = Object.entries(vars).map(([key, val]) => `  ${key}: ${val};`)
  return `/* Framer - ${preset} */
.trait-frame-wrapper > * {
${lines.join('\n')}
}`
}
